import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, InputNumber, message, Tag, Space, Typography, Popconfirm, Card, Checkbox, Divider, DatePicker, Row, Col, Switch } from 'antd';
import { CheckOutlined, DeleteOutlined, UserOutlined, ClockCircleOutlined, PlusOutlined, EditOutlined, LockOutlined } from '@ant-design/icons';
import api from '../utils/api';
import { PERMISSIONS, effectivePerms, ROLE_DEFAULT_PERMS, ROLE_COMPULSORY_PERMS } from '../utils/perms';
import { COMPANY_SITE_PERMS } from '../utils/companySites';

const { Title, Text } = Typography;
const { Option } = Select;

const fmtHr = (h) => {
  if (h === 0 || h === 24) return '12:00 AM (Midnight)';
  if (h === 12) return '12:00 PM (Noon)';
  if (h < 12) return `${h}:00 AM`;
  return `${h - 12}:00 PM`;
};

const roleLabels = {
  super_admin: 'Super Admin', admin: 'Admin', manager: 'Manager', hr_manager: 'HR Manager', viewer: 'Viewer',
  graphic_designer: 'Graphic Designer', video_editor: 'Video Editor',
  distribution_staff: 'Distribution Staff', poster_viewer: 'Poster Viewer',
  script_writer: 'Script Writer', audio_voiceover: 'Audio Voiceover',
  youtube_manager: 'YouTube Manager', content_researcher: 'Content Researcher'
};

// Staff-level permissions that HR can assign (no financial/HR-level access)
const STAFF_ONLY_PERMS = ['salary_view', 'thumbnails', 'thumbnail_download', 'thumbnail_upload', 'audio_upload', 'audio_download', 'video_download', 'video_edit_log', 'ai_audio_upload', 'ai_audio_download', 'ai_assistant', 'ai_tools', ...COMPANY_SITE_PERMS];

export default function Users({ currentUser }) {
  const isCurrentAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin' || currentUser?.role === 'manager';
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const canEditAccessRules = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [activityLog, setActivityLog] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [logFilters, setLogFilters] = useState({ user_id: '', action_type: '', from: '', to: '', search: '' });
  const [loading, setLoading] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [permModal, setPermModal] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [permSelected, setPermSelected] = useState([]);
  const [officeStart, setOfficeStart] = useState(9);
  const [officeEnd, setOfficeEnd] = useState(19);
  const [loginAnywhere, setLoginAnywhere] = useState(false);
  const [loginSunday, setLoginSunday] = useState(false);
  const [qrRadius, setQrRadius] = useState(0);
  const [editStaffId, setEditStaffId] = useState('');
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editUsername, setEditUsername] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editCompanyId, setEditCompanyId] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => { fetchUsers(); fetchActivity(); fetchActionTypes(); fetchCompanies(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const fetchCompanies = async () => {
    try { const res = await api.get('/companies'); setCompanies(res.data); } catch (err) { console.error(err); }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try { const res = await api.get('/auth/users'); setUsers(res.data); } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchActivity = async (filters) => {
    const f = filters || logFilters;
    try {
      const res = await api.get('/reports/activity-log', { params: { limit: 200, user_id: f.user_id, action_type: f.action_type, from: f.from, to: f.to, search: f.search } });
      setActivityLog(res.data);
    } catch (err) { console.error(err); }
  };
  const fetchActionTypes = async () => {
    try { const res = await api.get('/reports/activity-actions'); setActionTypes(res.data || []); } catch (err) { console.error(err); }
  };
  const updateLogFilter = (patch) => { const next = { ...logFilters, ...patch }; setLogFilters(next); fetchActivity(next); };
  const clearLogFilters = () => { const next = { user_id: '', action_type: '', from: '', to: '', search: '' }; setLogFilters(next); fetchActivity(next); };

  const handleCreateUser = async (values) => {
    try {
      const res = await api.post('/auth/create-user', { ...values, permissions: values.permissions || [], office_hours_start: values.office_hours_start ?? 9, office_hours_end: values.office_hours_end ?? 19 });
      const d = res.data || {};
      message.success('User created successfully!');
      // Tell admin what happened with the login email.
      if (d.email_requested) {
        if (d.email_sent) {
          Modal.success({
            title: 'Login details emailed',
            content: `Login ID + password sent to ${d.email}.`
          });
        } else {
          Modal.warning({
            title: 'User created — email not sent',
            content: (
              <div>
                <p>Email could not be sent ({d.email_error || 'email server not set up'}). Please share these login details with the user manually:</p>
                <p style={{ background: '#f6f6f6', padding: 10, borderRadius: 6 }}>
                  <b>Login:</b> https://data.intubemedia.com/login<br />
                  <b>Username:</b> {values.username}<br />
                  <b>Password:</b> {values.password}
                </p>
              </div>
            )
          });
        }
      }
      setCreateModal(false);
      form.resetFields();
      fetchUsers();
    } catch (err) { message.error(err.response?.data?.error || 'Failed to create user'); }
  };

  const openPermModal = (u) => {
    setPermUser(u);
    setPermSelected(effectivePerms(u));
    setOfficeStart(u.office_hours_start ?? 9);
    setOfficeEnd(u.office_hours_end ?? 19);
    setLoginAnywhere(!!u.login_anywhere);
    setLoginSunday(!!u.login_sunday);
    setQrRadius(Number(u.qr_radius_m) || 0);
    setEditStaffId(u.staff_id || '');
    setEditName(u.full_name || '');
    setEditEmail(u.email || '');
    setEditUsername(u.username || '');
    setEditPassword('');
    setEditCompanyId(u.company_id ?? null);
    setPermModal(true);
  };

  const handleSavePerms = async () => {
    try {
      await api.put(`/auth/users/${permUser.id}/permissions`, { permissions: permSelected });
      await api.put(`/auth/users/${permUser.id}/office-hours`, { office_hours_start: officeStart, office_hours_end: officeEnd });
      if (canEditAccessRules) {
        await api.put(`/auth/users/${permUser.id}/access-rules`, { login_anywhere: loginAnywhere ? 1 : 0, login_sunday: loginSunday ? 1 : 0, qr_radius_m: Number(qrRadius) || 0 });
      }
      await api.put(`/auth/users/${permUser.id}/staff-id`, { staff_id: editStaffId });
      if ((editCompanyId ?? null) !== (permUser.company_id ?? null)) {
        await api.put(`/auth/users/${permUser.id}/company`, { company_id: editCompanyId ?? null });
      }
      if (editName !== permUser.full_name || editEmail !== permUser.email) {
        await api.put(`/auth/users/${permUser.id}/profile`, { full_name: editName, email: editEmail });
      }
      // Update username/password if changed
      if (editUsername !== permUser.username || editPassword) {
        const credPayload = {};
        if (editUsername !== permUser.username) credPayload.username = editUsername;
        if (editPassword) credPayload.password = editPassword;
        const credRes = await api.put(`/auth/users/${permUser.id}/credentials`, credPayload);
        if (credRes.data?.email_sent) message.success('Login details emailed to staff!');
      }
      message.success('Permissions & details updated!');
      setPermModal(false);
      setPermUser(null);
      fetchUsers();
    } catch (err) { message.error(err.response?.data?.error || 'Failed to update'); }
  };

  const handleApprove = async (id) => {
    try { await api.put(`/auth/users/${id}/approve`); message.success('User approved!'); fetchUsers(); }
    catch (err) { message.error('Failed'); }
  };

  const handleRoleChange = async (id, role) => {
    try {
      await api.put(`/auth/users/${id}/role`, { role });
      const defaults = ROLE_DEFAULT_PERMS[role] || [];
      if (defaults.length) await api.put(`/auth/users/${id}/permissions`, { permissions: defaults });
      message.success('Role updated!');
      fetchUsers();
    } catch (err) { message.error('Failed'); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/auth/users/${id}`); message.success('User deleted'); fetchUsers(); }
    catch (err) { message.error(err.response?.data?.error || 'Failed'); }
  };

  const userColumns = [
    { title: 'Name', dataIndex: 'full_name', key: 'name', fixed: 'left', width: 150 },
    { title: 'Staff ID', dataIndex: 'staff_id', key: 'staff_id', width: 100, render: v => v ? <Tag color="purple">{v}</Tag> : <Text type="secondary">—</Text> },
    { title: 'Company', dataIndex: 'company_name', key: 'company', width: 160, render: v => v ? <Tag color="geekblue">{v}</Tag> : <Text type="secondary">—</Text> },
    { title: 'Username', dataIndex: 'username', key: 'username', width: 130 },
    { title: 'Email', dataIndex: 'email', key: 'email', width: 200 },
    { title: 'Role', dataIndex: 'role', key: 'role', width: 170, render: (r, record) => {
      if (record.role === 'super_admin' && !isSuperAdmin) return <Tag color="red">Super Admin</Tag>;
      const availableRoles = isSuperAdmin ? roleLabels : Object.fromEntries(Object.entries(roleLabels).filter(([k]) => k !== 'super_admin'));
      return (
        <Select value={r} onChange={(v) => handleRoleChange(record.id, v)} size="small" style={{ width: 160 }}>
          {Object.entries(availableRoles).map(([k, v]) => <Option key={k} value={k}>{v}</Option>)}
        </Select>
      );
    }},
    {
      title: 'Status', dataIndex: 'is_approved', key: 'status', width: 100,
      render: (v) => v ? <Tag color="green">Approved</Tag> : <Tag color="orange">Pending</Tag>
    },
    {
      title: 'Access', key: 'permissions', width: 220,
      render: (_, r) => r.role === 'super_admin'
        ? <Tag color="red">All access (Super Admin)</Tag>
        : r.role === 'admin'
        ? <Tag color="gold">All access (Admin)</Tag>
        : (Array.isArray(r.permissions) && r.permissions.length
            ? <Space size={[4, 4]} wrap>{r.permissions.map(p => <Tag key={p} color="blue">{(PERMISSIONS.find(x => x.key === p)?.label) || p}</Tag>)}</Space>
            : <Text type="secondary">No extra access</Text>)
    },
    { title: 'Office Hours', key: 'hours', width: 120,
      render: (_, r) => (r.role === 'admin' || r.role === 'super_admin') ? <Tag color="gold">24/7</Tag>
        : (r.office_hours_start === 0 && r.office_hours_end === 24) ? <Tag color="green">24/7</Tag>
        : <Tag color="blue"><ClockCircleOutlined /> {fmtHr(r.office_hours_start ?? 9)} — {fmtHr(r.office_hours_end ?? 19)}</Tag>
    },
    { title: 'Joined', dataIndex: 'created_at', key: 'date', width: 100, render: (v) => new Date(v).toLocaleDateString('en-IN') },
    {
      title: 'Actions', key: 'actions', fixed: 'right', width: 220,
      render: (_, r) => (
        <Space>
          {!r.is_approved && <Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => handleApprove(r.id)}>Approve</Button>}
          {(r.role === 'super_admin' && !isSuperAdmin) ? null : (r.role === 'admin' && !isSuperAdmin) ? null : <Button size="small" type="primary" ghost icon={<EditOutlined />} onClick={() => openPermModal(r)}>Edit Access</Button>}
          {(r.role === 'super_admin' && !isSuperAdmin) ? null : (
            <Popconfirm title={`Delete ${r.full_name}?`} onConfirm={() => handleDelete(r.id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  const activityColumns = [
    { title: 'User', dataIndex: 'full_name', key: 'user', render: (v, r) => v || r.username || 'System' },
    { title: 'Action', dataIndex: 'action', key: 'action', render: (v) => <Tag>{v}</Tag> },
    { title: 'Details', dataIndex: 'details', key: 'details', ellipsis: true },
    { title: 'Time', dataIndex: 'created_at', key: 'time', render: (v) => new Date(v).toLocaleString('en-IN') }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}><UserOutlined /> User Management</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setCreateModal(true); }}>Create User</Button>
      </div>

      <Card
        title="Users"
        extra={<Input.Search placeholder="Search name / staff ID / username" allowClear style={{ width: 300 }} value={userSearch} onChange={(e) => setUserSearch(e.target.value)} />}
        style={{ borderRadius: 12, marginBottom: 24 }}
      >
        <Table
          dataSource={(() => {
            const q = userSearch.trim().toLowerCase();
            if (!q) return users;
            return users.filter(u =>
              (u.full_name || '').toLowerCase().includes(q) ||
              (u.staff_id || '').toLowerCase().includes(q) ||
              (u.username || '').toLowerCase().includes(q) ||
              (u.email || '').toLowerCase().includes(q)
            );
          })()}
          columns={userColumns} rowKey="id" loading={loading} size="middle" scroll={{ x: 1400 }}
        />
      </Card>

      <Card title={<><ClockCircleOutlined /> Activity Log</>} style={{ borderRadius: 12 }}>
        <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
          <Col xs={24} sm={5}>
            <Select placeholder="All users" value={logFilters.user_id || undefined} onChange={(v) => updateLogFilter({ user_id: v || '' })} allowClear showSearch optionFilterProp="children" style={{ width: '100%' }}>
              {users.map(u => <Select.Option key={u.id} value={u.id}>{u.full_name}</Select.Option>)}
            </Select>
          </Col>
          <Col xs={24} sm={5}>
            <Select placeholder="All actions" value={logFilters.action_type || undefined} onChange={(v) => updateLogFilter({ action_type: v || '' })} allowClear showSearch style={{ width: '100%' }}>
              {actionTypes.map(a => <Select.Option key={a} value={a}>{a}</Select.Option>)}
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <DatePicker.RangePicker style={{ width: '100%' }} format="YYYY-MM-DD"
              onChange={(d) => updateLogFilter({ from: d && d[0] ? d[0].format('YYYY-MM-DD') : '', to: d && d[1] ? d[1].format('YYYY-MM-DD') : '' })} />
          </Col>
          <Col xs={24} sm={5}>
            <Input.Search placeholder="Search details" allowClear onSearch={(v) => updateLogFilter({ search: v })} />
          </Col>
          <Col xs={24} sm={3}>
            <Button onClick={clearLogFilters} style={{ width: '100%' }}>Clear</Button>
          </Col>
        </Row>
        <Table dataSource={activityLog} columns={activityColumns} rowKey="id" size="small" pagination={{ pageSize: 20 }} />
      </Card>

      <Modal title="Create New User" open={createModal} onCancel={() => setCreateModal(false)} onOk={() => form.submit()} okText="Create User">
        <Form form={form} layout="vertical" onFinish={handleCreateUser}>
          <Form.Item name="full_name" label="Full Name" rules={[{ required: true, message: 'Name required' }]}>
            <Input placeholder="Enter full name" />
          </Form.Item>
          <Form.Item name="staff_id" label="Staff ID" extra="e.g. BMW001, BMW002 — unique ID for this staff member">
            <Input placeholder="Enter staff ID (e.g. BMW001)" />
          </Form.Item>
          <Form.Item name="username" label="Username" rules={[{ required: true, message: 'Username required' }]}>
            <Input placeholder="Enter username" />
          </Form.Item>
          <Form.Item name="email" label="Email" extra="If a real email is entered, the login ID + password are emailed to the user automatically.">
            <Input placeholder="Enter email (login details will be sent here)" />
          </Form.Item>
          <Form.Item name="password" label="Password" rules={[{ required: true, message: 'Password required' }]}>
            <Input.Password placeholder="Enter password" />
          </Form.Item>
          <Form.Item name="role" label="Role" rules={[{ required: true, message: 'Select role' }]} initialValue="viewer">
            <Select onChange={(role) => { const defaults = ROLE_DEFAULT_PERMS[role] || []; form.setFieldsValue({ permissions: defaults }); }}>
              {Object.entries(isSuperAdmin ? roleLabels : Object.fromEntries(Object.entries(roleLabels).filter(([k]) => k !== 'super_admin'))).map(([k, v]) => <Option key={k} value={k}>{v}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="company_id" label="Company (Optional)">
            <Select allowClear placeholder="Select company">
              {companies.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
            </Select>
          </Form.Item>
          <Divider style={{ margin: '12px 0' }}>Feature Access (optional)</Divider>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
            Tick which features this user can use.
          </Text>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.role !== cur.role}>
            {({ getFieldValue }) => {
              const role = getFieldValue('role') || 'viewer';
              const compulsory = ROLE_COMPULSORY_PERMS[role] || [];
              return (
                <Form.Item name="permissions" initialValue={[]} style={{ marginBottom: 0 }}>
                  <Checkbox.Group style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {(isCurrentAdmin ? PERMISSIONS : PERMISSIONS.filter(p => STAFF_ONLY_PERMS.includes(p.key))).map(p =>
                      <Checkbox key={p.key} value={p.key} disabled={compulsory.includes(p.key)}>{p.label}{compulsory.includes(p.key) ? ' (compulsory)' : ''}</Checkbox>
                    )}
                  </Checkbox.Group>
                </Form.Item>
              );
            }}
          </Form.Item>
          <Divider style={{ margin: '12px 0' }}><ClockCircleOutlined /> Office Hours (Dashboard Access Time)</Divider>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
            Staff sirf isi time ke beech dashboard access kar sakta hai. 24/7 access ke liye Start=0, End=24 karo.<br/>
            <b>Note:</b> 0 = 12:00 AM (Midnight), 9 = 9:00 AM, 12 = 12:00 PM (Noon), 19 = 7:00 PM
          </Text>
          <Space>
            <Form.Item name="office_hours_start" label="Start Hour (24h)" initialValue={9} style={{ marginBottom: 0 }}>
              <InputNumber min={0} max={23} style={{ width: 80 }} />
            </Form.Item>
            <Form.Item name="office_hours_end" label="End Hour (24h)" initialValue={19} style={{ marginBottom: 0 }}>
              <InputNumber min={1} max={24} style={{ width: 80 }} />
            </Form.Item>
          </Space>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const s = getFieldValue('office_hours_start') ?? 9;
              const e = getFieldValue('office_hours_end') ?? 19;
              return <div style={{ marginTop: 6 }}><Tag color="blue">{fmtHr(s)} — {fmtHr(e)}</Tag>{s===0 && e===24 && <Tag color="green">24/7</Tag>}</div>;
            }}
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Feature Access${permUser ? ` — ${permUser.full_name}` : ''}`}
        open={permModal}
        onCancel={() => setPermModal(false)}
        onOk={handleSavePerms}
        okText="Save Access & Hours"
      >
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Name & Email</Text>
        <Space direction="vertical" style={{ width: '100%', marginBottom: 12 }}>
          <Input prefix={<UserOutlined />} placeholder="Full Name" value={editName} onChange={e => setEditName(e.target.value)} />
          <Input prefix={<EditOutlined />} placeholder="Email" value={editEmail} onChange={e => setEditEmail(e.target.value)} />
        </Space>

        <Divider />
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Company (kis company ka staff hai)</Text>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          Isse Staff Tasks me staff company-wise alag dikhta hai — Bainsla Music wale ko channel-wise task, Intube Media wale ko pehle jaisa.
        </Text>
        <Select
          allowClear showSearch optionFilterProp="children"
          placeholder="Select company"
          value={editCompanyId ?? undefined}
          onChange={(v) => setEditCompanyId(v ?? null)}
          style={{ width: '100%', marginBottom: 12 }}
        >
          {companies.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
        </Select>

        <Divider />
        <Text strong style={{ display: 'block', marginBottom: 8 }}><LockOutlined /> Login Credentials</Text>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          Username ya password change karo — staff ki email pe naye login details automatically jayenge.
        </Text>
        <Space direction="vertical" style={{ width: '100%', marginBottom: 12 }}>
          <Input prefix={<UserOutlined />} placeholder="Username" value={editUsername} onChange={e => setEditUsername(e.target.value)} />
          <Input.Password prefix={<LockOutlined />} placeholder="New Password (blank = no change)" value={editPassword} onChange={e => setEditPassword(e.target.value)} />
        </Space>

        <Divider />
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
          Tick which features this user can use. (Thumbnail UPLOAD is reserved for Admin & Graphic Designer only.)
        </Text>
        <Checkbox.Group value={permSelected} onChange={(vals) => { const compulsory = ROLE_COMPULSORY_PERMS[permUser?.role] || []; const merged = [...new Set([...vals, ...compulsory])]; setPermSelected(merged); }} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {(isCurrentAdmin ? PERMISSIONS : PERMISSIONS.filter(p => STAFF_ONLY_PERMS.includes(p.key))).map(p => {
            const compulsory = ROLE_COMPULSORY_PERMS[permUser?.role] || [];
            return <Checkbox key={p.key} value={p.key} disabled={compulsory.includes(p.key)}>{p.label}{compulsory.includes(p.key) ? ' (compulsory)' : ''}</Checkbox>;
          })}
        </Checkbox.Group>

        <Divider />
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Staff ID</Text>
        <Input placeholder="e.g. BMW001" value={editStaffId} onChange={e => setEditStaffId(e.target.value)} style={{ width: 200, marginBottom: 12 }} />

        <Divider />
        <Text strong style={{ display: 'block', marginBottom: 8 }}><ClockCircleOutlined /> Office Hours (Dashboard Access Time)</Text>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
          Staff sirf isi time ke beech dashboard access kar sakta hai. 24 ghante access ke liye Start=0, End=24 karo.<br/>
          <b>Note:</b> 0 = 12:00 AM (Midnight), 9 = 9:00 AM, 12 = 12:00 PM (Noon), 19 = 7:00 PM
        </Text>
        <Space>
          <span>Start (24h): <InputNumber min={0} max={23} value={officeStart} onChange={v => setOfficeStart(v)} style={{ width: 80 }} /></span>
          <span>End (24h): <InputNumber min={1} max={24} value={officeEnd} onChange={v => setOfficeEnd(v)} style={{ width: 80 }} /></span>
        </Space>
        <div style={{ marginTop: 8 }}>
          <Tag color="blue">{fmtHr(officeStart)} — {fmtHr(officeEnd)}</Tag>
          {officeStart === 0 && officeEnd === 24 && <Tag color="green">24/7 Access</Tag>}
        </div>

        {canEditAccessRules && (<>
          <Divider />
          <Text strong style={{ display: 'block', marginBottom: 8 }}>📍 Location & Sunday Access</Text>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
            Default: staff sirf office ke <b>500m</b> ke andar hi login kar sakta hai, aur <b>Sunday</b> ko dashboard band rehta hai. Kisi staff ko chhoot deni ho to yahan on karo.
          </Text>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space>
              <Switch checked={loginAnywhere} onChange={setLoginAnywhere} />
              <span>Kahin se bhi login (500m limit hatao)</span>
            </Space>
            <Space>
              <Switch checked={loginSunday} onChange={setLoginSunday} />
              <span>Sunday ko bhi access de do</span>
            </Space>
            <Space>
              <span>QR attendance radius:</span>
              <InputNumber min={0} max={100000} step={100} value={qrRadius} style={{ width: 130 }}
                onChange={v => setQrRadius(v || 0)} addonAfter="m" />
              <span style={{ color: '#888', fontSize: 12 }}>0 = office default</span>
            </Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Is staff ki attendance sirf itne metre ke andar se lagegi. Kisi ko 3 km ki chhoot deni ho to <b>3000</b> daal do — QR sirf 3 km ke andar se hi chalega. Salary/attendance ka baaki hisab waisa hi rehta hai.
            </Text>
          </Space>
        </>)}
      </Modal>
    </div>
  );
}
